import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen px-4 py-12 bg-gray-50 sm:px-6 lg:px-8">
      <div className="max-w-4xl p-8 mx-auto bg-white rounded-lg shadow-lg md:p-12">
        {/* Header */}
        <header className="pb-6 mb-8 border-b border-gray-200">
          <h1 className="mb-3 text-4xl font-bold text-gray-900">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: 20 January 2025</p>
        </header>

        {/* Introduction */}
        <section className="mb-8">
          <p className="mb-4 leading-relaxed text-gray-700">
            Welcome to Inhocsigno Hominum Salvator (ISA Academy) ("we", "our",
            or "us"). We value your privacy and are committed to protecting your
            personal information. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you visit our
            website isaacademy.co.id, purchase services, or participate in our
            trainings, consultations, and webinars.
          </p>
          <p className="leading-relaxed text-gray-700">
            By accessing our website or using our services, you agree to the
            terms of this Privacy Policy.
          </p>
        </section>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            1. Information We Collect
          </h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            We collect several types of information to provide and improve our
            services:
          </p>

          <div className="mb-4 ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              1.1 Personal Information
            </h3>
            <p className="mb-3 text-gray-700">
              When you register, purchase, or interact with our website, we may
              collect:
            </p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Billing address</li>
              <li>
                Payment information (processed by third-party payment providers;
                we do not store full credit card numbers)
              </li>
              <li>Account login credentials</li>
              <li>Company/organization information (if applicable)</li>
            </ul>
          </div>

          <div className="mb-4 ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              1.2 Usage Data
            </h3>
            <p className="mb-3 text-gray-700">
              We may automatically collect data on how you access and use the
              website, such as:
            </p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Referral URLs</li>
              <li>Cookies and tracking technologies</li>
            </ul>
          </div>

          <div className="ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              1.3 Training & Webinar Interaction Data
            </h3>
            <p className="mb-3 text-gray-700">
              When you join our training, consultation sessions, or webinars, we
              may collect:
            </p>
            <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Attendance data</li>
              <li>Chat interactions and Q&A submissions</li>
              <li>Feedback or survey responses</li>
              <li>Recorded sessions (if applicable and with notice)</li>
            </ul>
            <p className="italic text-gray-700">
              If recordings are used, we will notify participants beforehand.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            2. How We Use Your Information
          </h2>
          <p className="mb-3 text-gray-700">We use your information for:</p>
          <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Processing orders and payments</li>
            <li>Managing your account and service access</li>
            <li>Delivering training, webinar, and consultation services</li>
            <li>
              Sending service-related notifications (confirmations, reminders,
              updates)
            </li>
            <li>Customer support</li>
            <li>Improving our website and service offerings</li>
            <li>Sending marketing communications (with your consent)</li>
            <li>Preventing fraud and ensuring security</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            3. Sharing Your Information
          </h2>
          <p className="mb-4 text-gray-700">
            We may share your information with:
          </p>

          <div className="mb-4 ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              3.1 Service Providers
            </h3>
            <p className="mb-3 text-gray-700">Such as:</p>
            <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Payment processors</li>
              <li>Webinar platforms (e.g., Zoom, Google Meet)</li>
              <li>Email service providers</li>
              <li>Customer support tools</li>
              <li>Analytics providers</li>
            </ul>
            <p className="text-gray-700">
              These parties only access data necessary to perform their
              services.
            </p>
          </div>

          <div className="mb-4 ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              3.2 Legal Requirements
            </h3>
            <p className="mb-3 text-gray-700">
              We may disclose information when required to:
            </p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Comply with applicable laws</li>
              <li>Respond to legal requests</li>
              <li>Protect our rights or the safety of others</li>
            </ul>
          </div>

          <div className="ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              3.3 Business Transfers
            </h3>
            <p className="text-gray-700">
              If we merge, acquire, or sell assets, customer data may be
              transferred as part of the transaction.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            4. Cookies & Tracking Technologies
          </h2>
          <p className="mb-3 text-gray-700">
            We use cookies, web beacons, and similar technologies to:
          </p>
          <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Maintain website functionality</li>
            <li>Analyze traffic</li>
            <li>Store preferences</li>
            <li>Improve user experience</li>
          </ul>
          <p className="text-gray-700">
            You can manage cookies through your browser settings.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            5. Data Security
          </h2>
          <p className="mb-3 text-gray-700">
            We implement administrative, technical, and physical safeguards to
            protect your information.
          </p>
          <p className="text-gray-700">
            However, no method of transmission over the internet is completely
            secure. We cannot guarantee absolute security.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            6. Data Retention
          </h2>
          <p className="mb-3 text-gray-700">
            We retain your personal data only as long as necessary for:
          </p>
          <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Providing services</li>
            <li>Legal compliance</li>
            <li>Resolving disputes</li>
            <li>Enforcing agreements</li>
          </ul>
          <p className="text-gray-700">
            Training/webinar recordings, if applicable, are stored only for the
            declared purpose (e.g., playback for participants).
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            7. Your Rights
          </h2>
          <p className="mb-3 text-gray-700">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Access your personal data</li>
            <li>Update or correct information</li>
            <li>Request deletion</li>
            <li>Withdraw consent</li>
            <li>Object to processing</li>
            <li>Request a copy of your data</li>
          </ul>
          <p className="text-gray-700">
            To exercise your rights, contact us at{" "}
            <a
              href="mailto:company@sustainability.co.id"
              className="text-blue-600 underline hover:text-blue-800"
            >
              company@sustainability.co.id
            </a>
            .
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            8. Payment Information
          </h2>
          <p className="mb-3 text-gray-700">
            Payments are securely processed by trusted third-party payment
            providers such as:
          </p>
          <ul className="mb-3 ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Midtrans</li>
          </ul>
          <p className="text-gray-700">
            We do not store your full credit card details on our servers.
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            9. Third-Party Links
          </h2>
          <p className="text-gray-700">
            Our website may contain links to third-party sites. We are not
            responsible for their privacy practices or content.
          </p>
        </section>

        {/* Section 10 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            10. Children's Privacy
          </h2>
          <p className="mb-3 text-gray-700">
            Our services are not intended for individuals under 18.
          </p>
          <p className="text-gray-700">
            We do not knowingly collect information from children.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            11. International Data Transfers
          </h2>
          <p className="text-gray-700">
            If you access our website from outside Indonesia, your information
            may be transferred to and processed in jurisdictions with different
            data protection laws.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            12. Changes to This Privacy Policy
          </h2>
          <p className="mb-3 text-gray-700">
            We may update this Privacy Policy at any time.
          </p>
          <p className="text-gray-700">
            Changes will be posted on this page with a revised "Last Updated"
            date.
          </p>
        </section>

        {/* Section 13 - Contact */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            13. Contact Us
          </h2>
          <p className="mb-4 text-gray-700">
            If you have any questions about this Privacy Policy or how your
            information is handled, please contact us at:
          </p>
          <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
            <p className="mb-2 font-medium text-gray-900">
              PT. Inhocsigno Hominum Salvator
            </p>
            <p className="mb-1 text-gray-700">
              <span className="font-medium">Email:</span>{" "}
              <a
                href="mailto:emil@sustainability.co.id"
                className="text-blue-600 underline hover:text-blue-800"
              >
                emil@sustainability.co.id
              </a>
            </p>
            <p className="mb-1 text-gray-700">
              <span className="font-medium">Phone:</span>{" "}
              <a
                href="tel:+6281212276133"
                className="text-blue-600 hover:text-blue-800"
              >
                +62 812-1227-6133
              </a>
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Website:</span>{" "}
              <a
                href="https://isaacademy.co.id"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                isaacademy.co.id
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
